import { Box, List, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { Size } from '~/types/ThemeTypes'

interface ReleaseEmailPreviewProps {
  isOpen: boolean
  onClose: () => void
}

// The address the desktop-release-notifier sends from (mailgun_from_address is
// the same across all envs — see the service's terraform.tfvars). Surfaced in
// the preview so users can add it to their contacts / safelist it.
const SENDER_ADDRESS = 'releases@messages.mangovoice.com'

// Illustrative sample that mirrors the desktop-release-notifier email format
// (see the service's lib/email.ts: "<product> <version> is now available"
// subject, the version/channel line, a "What's new" section, and an unsubscribe
// footer). Kept in English because the service sends English email regardless of
// the app's locale — the modal note below sets that expectation. The unsubscribe
// line is shown as a plain label rather than a literal URL: the real link is
// region-specific (app.mangovoice.com vs app.mangovoice.co.uk) and also surfaced
// via the mail client's native unsubscribe button, so a literal URL here would
// be both noisy and potentially wrong for the viewer's region.
const SAMPLE_EMAIL = {
  from: `"Mango Voice" <${SENDER_ADDRESS}>`,
  subject: `Mango Voice 2.4.0 is now available`,
  body: [
    `A new version of Mango Voice (2.4.0) has been released on the prod channel.`,
    ``,
    `What's new:`,
    `• Faster call connection and fewer dropped calls`,
    `• Voicemail transcription accuracy improvements`,
    `• Bug fixes and stability improvements`,
    ``,
    `Unsubscribe from these emails`,
  ].join('\n'),
}

const FieldRow = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Text as="span" textStyle="Body4" color="fg.muted">
      {label}{' '}
    </Text>
    <Text as="span" textStyle="Body4" color="fg.default">
      {value}
    </Text>
  </Box>
)

export const ReleaseEmailPreview = ({
  isOpen,
  onClose,
}: ReleaseEmailPreviewProps) => {
  const { t } = useTranslation()

  return (
    <MangoModal
      isOpen={isOpen}
      onClose={onClose}
      size={Size.md}
      header={t('updateSettings.releaseNotifications.previewModalTitle')}
      body={
        <VStack align="stretch" gap="16px">
          <Text textStyle="Body4" color="fg.muted">
            {t('updateSettings.releaseNotifications.previewModalNote')}
          </Text>
          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="8px"
            overflow="hidden"
          >
            <VStack
              align="stretch"
              gap="2px"
              bg="bg.subtle"
              px="16px"
              py="12px"
            >
              <FieldRow
                label={t('updateSettings.releaseNotifications.previewFrom')}
                value={SAMPLE_EMAIL.from}
              />
              <FieldRow
                label={t('updateSettings.releaseNotifications.previewSubject')}
                value={SAMPLE_EMAIL.subject}
              />
            </VStack>
            <Text
              px="16px"
              py="16px"
              textStyle="Body3"
              color="fg.default"
              whiteSpace="pre-wrap"
            >
              {SAMPLE_EMAIL.body}
            </Text>
          </Box>

          <VStack align="stretch" gap="6px">
            <Text textStyle="Body3_Bold" color="fg.default">
              {t('updateSettings.releaseNotifications.previewTipsTitle')}
            </Text>
            <Text textStyle="Body4" color="fg.muted">
              {t('updateSettings.releaseNotifications.previewSenderTip')}{' '}
              <Text as="span" textStyle="Body4_Bold" color="fg.default">
                {SENDER_ADDRESS}
              </Text>
            </Text>
            <List.Root textStyle="Body4" color="fg.muted" ps="18px">
              <List.Item>
                {t('updateSettings.releaseNotifications.previewTipContacts')}
              </List.Item>
              <List.Item>
                {t('updateSettings.releaseNotifications.previewTipJunk')}
              </List.Item>
              <List.Item>
                {t('updateSettings.releaseNotifications.previewTipSafelist')}
              </List.Item>
            </List.Root>
          </VStack>
        </VStack>
      }
      footer={
        <MangoButton
          size={Size.sm}
          variant={MangoButtonVariant.Default_200}
          onClick={onClose}
        >
          {t('updateSettings.releaseNotifications.previewClose')}
        </MangoButton>
      }
    />
  )
}
